import { useEffect, useState } from "react";
import { ShoppingBag, Truck, PackageCheck, XCircle, CheckCircle2, IndianRupee, Clock, Wallet, Percent } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/constants/branding";
import { StatCard } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusPill, Empty, DataTable, FilterTabs, SectionTitle, Field, fmtDate, fmtDateTime } from "@/components/dash";

const ORDER_FILTERS = ["all", "pending", "confirmed", "shipped", "delivered", "cancelled"];

export function VendorOrdersPanel({ orders, reload }) {
  const [filter, setFilter] = useState("all");
  const [ship, setShip] = useState(null);
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);

  const update = async (order, status, extra = {}) => {
    setBusy(true);
    try {
      await api.put(`/vendor/orders/${order.id}/status`, { status, ...extra });
      toast.success(`Order marked ${status}`);
      setShip(null);
      await reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const visible = orders.filter((o) => filter === "all" || o.vendor_status === filter);
  const count = (f) => (f === "all" ? orders.length : orders.filter((o) => o.vendor_status === f).length);

  return (
    <div>
      <SectionTitle title={`Orders (${orders.length})`} desc="Confirm, ship and deliver the items from your store. Customers are notified of every step." />
      <FilterTabs value={filter} onChange={setFilter} options={ORDER_FILTERS.map((f) => ({ key: f, label: f[0].toUpperCase() + f.slice(1), count: count(f) }))} />
      {visible.length === 0 ? <Empty icon={ShoppingBag} title="No orders here" desc="New orders will show up automatically." /> : (
        <div className="space-y-3">
          {visible.map((o) => {
            const st = o.vendor_status;
            return (
              <div key={o.id} className="bg-white rounded-xl border border-border p-4" data-testid={`vendor-order-${o.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-bold">{o.order_number}</p>
                    <p className="text-xs text-muted-foreground">{fmtDateTime(o.created_at)} · {o.customer_name} · {o.payment_method?.toUpperCase()}</p>
                  </div>
                  <StatusPill status={st} />
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  {o.items.map((it) => (
                    <div key={it.id || it.product_id} className="flex items-center gap-2 justify-between">
                      <span className="flex items-center gap-2 min-w-0">
                        {it.image && <img src={it.image} alt="" className="h-8 w-8 rounded object-cover shrink-0" />}
                        <span className="truncate text-muted-foreground">{it.name} × {it.qty}</span>
                        {it.fulfillment_status === "cancelled" && <span className="text-xs text-red-600">(cancelled)</span>}
                      </span>
                      <span>{formatINR(it.line_total)}</span>
                    </div>
                  ))}
                </div>
                {st !== "cancelled" && o.address && (
                  <div className="mt-3 text-xs text-muted-foreground bg-slate-50 rounded-lg p-3">
                    <span className="font-medium text-foreground">Ship to:</span> {o.address.name}, {o.address.line1}{o.address.line2 ? `, ${o.address.line2}` : ""}, {o.address.city}, {o.address.state} {o.address.pincode} · {o.address.phone}
                  </div>
                )}
                {o.items.find((i) => i.tracking_id) && (
                  <p className="text-xs text-muted-foreground mt-2">Tracking: {o.items.find((i) => i.tracking_id).courier} · {o.items.find((i) => i.tracking_id).tracking_id}</p>
                )}
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm">Your earning: <b>{formatINR(o.vendor_earning)}</b> <span className="text-xs text-muted-foreground">(after commission)</span></p>
                  <div className="flex gap-2">
                    {st === "pending" && <Button size="sm" disabled={busy} onClick={() => update(o, "confirmed")} data-testid={`order-confirm-${o.id}`}><CheckCircle2 className="h-4 w-4 mr-1" /> Confirm</Button>}
                    {st === "confirmed" && <Button size="sm" disabled={busy} onClick={() => { setCourier(""); setTracking(""); setShip(o); }} data-testid={`order-ship-${o.id}`}><Truck className="h-4 w-4 mr-1" /> Mark shipped</Button>}
                    {st === "shipped" && <Button size="sm" disabled={busy} onClick={() => update(o, "delivered")} data-testid={`order-deliver-${o.id}`}><PackageCheck className="h-4 w-4 mr-1" /> Mark delivered</Button>}
                    {(st === "pending" || st === "confirmed") && (
                      <Button size="sm" variant="outline" className="text-destructive" disabled={busy} data-testid={`order-cancel-${o.id}`}
                        onClick={() => window.confirm("Cancel these items? Stock will be restored.") && update(o, "cancelled")}>
                        <XCircle className="h-4 w-4 mr-1" /> Cancel
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!ship} onOpenChange={(v) => !v && setShip(null)}>
        <DialogContent data-testid="ship-dialog">
          <DialogHeader><DialogTitle>Ship {ship?.order_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Courier / delivery partner"><Input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="e.g. Delhivery, DTDC, India Post" data-testid="ship-courier" /></Field>
            <Field label="Tracking ID / AWB"><Input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="ship-tracking" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShip(null)}>Cancel</Button>
            <Button disabled={busy || !courier.trim() || !tracking.trim()} onClick={() => update(ship, "shipped", { courier: courier.trim(), tracking_id: tracking.trim() })} data-testid="ship-confirm">Mark shipped</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function VendorEarningsPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/vendor/earnings").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="vendor-earnings">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Available to withdraw" value={formatINR(d.available_balance)} icon={Wallet} />
        <StatCard label={`On hold (${d.hold_days}-day window)`} value={formatINR(d.on_hold)} icon={Clock} />
        <StatCard label="Paid out so far" value={formatINR(d.paid_out)} icon={IndianRupee} />
        <StatCard label="Your commission rate" value={`${d.commission_rate}%`} icon={Percent} />
      </div>
      <div className="bg-white rounded-xl border border-border p-4 text-sm text-muted-foreground">
        Gross sales <b className="text-foreground">{formatINR(d.gross_sales)}</b> − platform commission <b className="text-foreground">{formatINR(d.commission)}</b> = net earnings <b className="text-foreground">{formatINR(d.net_earnings)}</b>.
        Earnings are released {d.hold_days} days after delivery and paid out to your registered bank account.
      </div>
      <div>
        <SectionTitle title="Delivered items" />
        <DataTable head={["Order", "Product", "Sale", "Commission", "Net", "Delivered", "Status"]} empty="No delivered orders yet."
          rows={d.ledger.map((l) => [l.order_number, <span className="line-clamp-1 max-w-[200px]">{l.product} × {l.qty}</span>, formatINR(l.line_total), `− ${formatINR(l.commission)}`, <b>{formatINR(l.net)}</b>, fmtDate(l.delivered_at), l.released ? <StatusPill status="approved" /> : <span className="text-xs text-amber-700">On hold</span>])} />
      </div>
      <div>
        <SectionTitle title="Payout history" />
        <DataTable head={["Date", "Amount", "Method", "Reference"]} empty="No payouts yet."
          rows={d.payouts.map((p) => [fmtDate(p.created_at), <b>{formatINR(p.amount)}</b>, p.method.replace("_", " "), <span className="font-mono text-xs">{p.reference}</span>])} />
      </div>
    </div>
  );
}

export function VendorStorePanel() {
  const [info, setInfo] = useState(null);
  const [f, setF] = useState({ description: "", contact_phone: "", address_line1: "", city: "", state: "", pincode: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vendor/application").then(({ data }) => {
      setInfo(data);
      const a = data.application;
      if (a) setF({ description: a.description || "", contact_phone: a.contact_phone, address_line1: a.address_line1, city: a.city, state: a.state, pincode: a.pincode });
    }).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!info) return <p className="text-muted-foreground">Loading…</p>;
  const a = info.application;
  if (!a) return <Empty title="No store profile on file" desc="This account was created before seller onboarding. Contact support to add your business details." />;

  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put("/vendor/store", f); toast.success("Store details updated"); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-5xl" data-testid="vendor-store">
      <form onSubmit={save} className="bg-white rounded-xl border border-border p-5 space-y-4">
        <h3 className="font-semibold">{a.store_name}</h3>
        <Field label="About your store"><Textarea value={f.description} onChange={set("description")} rows={3} maxLength={500} /></Field>
        <Field label="Contact number"><Input value={f.contact_phone} onChange={set("contact_phone")} /></Field>
        <Field label="Pickup address"><Input value={f.address_line1} onChange={set("address_line1")} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="City"><Input value={f.city} onChange={set("city")} /></Field>
          <Field label="State"><Input value={f.state} onChange={set("state")} /></Field>
          <Field label="Pincode"><Input value={f.pincode} onChange={set("pincode")} maxLength={6} /></Field>
        </div>
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
      </form>
      <div className="bg-white rounded-xl border border-border p-5 space-y-3 text-sm self-start">
        <h3 className="font-semibold">Verified business details</h3>
        {[["PAN", a.pan_number], ["GSTIN", a.gst_number || "Not provided"], ["Business type", a.business_type?.replace("_", " ")],
          ["Bank account", `${a.bank?.bank_name} · ••••${a.bank?.account_last4}`], ["IFSC", a.bank?.ifsc], ["Commission rate", `${info.commission_rate}%`]].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 border-b border-border/60 pb-2 last:border-0"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>
        ))}
        <p className="text-xs text-muted-foreground pt-1">PAN, GSTIN and bank details are locked after approval for payout safety. To change them, contact Zilkart support.</p>
      </div>
    </div>
  );
}
